import type { QueryClient } from "@tanstack/react-query";
import { useLayoutEffect } from "react";
import { businessShape, defaultLabels, worksTheDesk, type MyBusiness } from "@teamna/shared";
import type { AnalyticsRole } from "@teamna/shared/analytics";
import { setAnalyticsPlace, setReplayText } from "../analytics/web.ts";
import { queryKeys } from "../hooks/keys.ts";
import { setActiveBusinessId as setOwnerBusinessId } from "../hooks/use-owner.ts";
import { setStaffBusinessId } from "../hooks/use-staff.ts";
import { setActiveBusinessId as setMemberBusinessId } from "./active-business.ts";

/**
 * The places one account can open (prototype `teamna/prototypes/account-switch.html`, 2026-10-09).
 *
 * One account can run a club, coach at a club and be a parent at a club. Each of those, at each
 * business, is a place: the owner office of DR Sports Academy, the coach day at Blue Wave, the
 * parent home at Sunrise Aquatics. An account with two or more places picks one after sign-in
 * ("Where do you want to go?") and switches from the account menu on every screen.
 *
 * This device remembers the last place, per account, so opening the app again goes back there.
 */

export type PlaceKind = "owner" | "coach" | "parent";

export type Place = {
  /** `owner:<businessId>`, `coach:<businessId>` or `parent:<businessId>`. */
  id: string;
  kind: PlaceKind;
  business: MyBusiness;
  to: "/owner/home" | "/staff/today" | "/member/home";
};

const ORDER: Record<PlaceKind, number> = { owner: 0, coach: 1, parent: 2 };

/** Every place of the account: the office of each business it runs, the coach day of each where it is staff, the home of each where it is a member. */
export function placesFor(businesses: readonly MyBusiness[]): Place[] {
  const out: Place[] = [];
  for (const business of businesses) {
    // The front desk opens the office too: the desk part of it (T1).
    if (worksTheDesk(business.staffRole)) {
      out.push({ id: `owner:${business.businessId}`, kind: "owner", business, to: "/owner/home" });
    } else if (business.staffRole === "staff") {
      out.push({ id: `coach:${business.businessId}`, kind: "coach", business, to: "/staff/today" });
    }
    // A membership the account left is not a place.
    if (business.membershipStatus !== null && business.membershipStatus !== "left") {
      out.push({ id: `parent:${business.businessId}`, kind: "parent", business, to: "/member/home" });
    }
  }
  return out.sort((a, b) => ORDER[a.kind] - ORDER[b.kind] || a.business.name.localeCompare(b.business.name));
}

/** The analytics role of a place: an admin's office is `coowner`, the front desk's `frontdesk`, a coach day `coach`, a home `parent`. */
export function analyticsRoleOf(place: Place): AnalyticsRole {
  if (place.kind === "owner") {
    if (place.business.staffRole === "frontdesk") return "frontdesk";
    return place.business.staffRole === "admin" ? "coowner" : "owner";
  }
  return place.kind === "coach" ? "coach" : "parent";
}

/** Session replay shows the text of the screens only in the team's places of a pilot business, never in a parent's home. */
export function replayShowsText(place: Place): boolean {
  return place.kind !== "parent" && place.business.pilotReplay === true;
}

/**
 * Points the area at the place's business, so the office, the coach day or the home opens on it.
 * Later analytics events carry that business, role and business shape, and session replay shows the
 * text of the screens when the place is the team's side of a pilot business.
 */
let appliedPlaceId: string | null = null;

export function applyPlace(place: Place): void {
  if (place.kind === "owner") setOwnerBusinessId(place.business.businessId);
  else if (place.kind === "coach") setStaffBusinessId(place.business.businessId);
  else setMemberBusinessId(place.business.businessId);
  markPlace(place);
}

function markPlace(place: Place): void {
  appliedPlaceId = place.id;
  setAnalyticsPlace({
    businessId: place.business.businessId,
    role: analyticsRoleOf(place),
    shape: businessShape(place.business),
    replayText: replayShowsText(place),
  });
}

/**
 * The place of the area that is open, from the business the area shows. The landing route and the
 * account menu call `applyPlace`, but a deep open does not go through them: a reload, a link, or iOS
 * Safari that opens the last page again (`/staff/today`). Before this, a deep open had no place, so
 * its events had no business and the replays of a pilot team stayed masked (2026-10-25).
 */
export function followAreaPlace(kind: PlaceKind, business: MyBusiness | null): void {
  const place = business ? placesFor([business]).find((candidate) => candidate.kind === kind) : undefined;
  if (place && place.id !== appliedPlaceId) markPlace(place);
}

/**
 * Each area layout (office, coach day, parent home) calls it with the business it shows. A layout
 * effect, so a new business (another office picked in the account menu) changes the text gate in
 * the same commit as its page, before the recorder reads the new screen (review F1.1).
 */
export function useAreaPlace(kind: PlaceKind, business: MyBusiness | null): void {
  useLayoutEffect(() => followAreaPlace(kind, business), [kind, business]);
}

const AREAS: readonly (readonly [string, PlaceKind])[] = [
  ["/owner", "owner"],
  ["/staff", "coach"],
  ["/member", "parent"],
];

/** The area of a Desk path: the office, the coach day or the parent home. Null for any other page. */
export function areaOf(pathname: string): PlaceKind | null {
  return AREAS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1] ?? null;
}

/**
 * Before a route of another area renders (Back from the parent home to the office, then Forward):
 * the place applied now is not the place of that area, so the text gate closes at once and the
 * area layout sets the right place (review F1.1). A page outside the three areas keeps the place.
 */
export function enterArea(pathname: string): void {
  const kind = areaOf(pathname);
  if (kind === null || appliedPlaceId === null || appliedPlaceId.startsWith(`${kind}:`)) return;
  appliedPlaceId = null;
  setReplayText(false);
}

/**
 * After each read of `/me/businesses`: the place that is open now keeps text in replay only while
 * the new rows still allow it. A place that is gone, or a pilot that ended, goes back to masked.
 */
export function syncPlaceReplay(businesses: readonly MyBusiness[]): void {
  if (appliedPlaceId === null) return;
  const place = placesFor(businesses).find((candidate) => candidate.id === appliedPlaceId);
  setReplayText(place ? replayShowsText(place) : false);
}

/**
 * Gives each new `/me/businesses` list to `syncPlaceReplay`, so a pilot that ended while the tab is
 * open goes back to masked (review F1.2). A failed read masks too, until a list says the pilot is
 * still on (review F2.3). Returns the unsubscribe.
 */
export function followBusinessesForReplay(queryClient: QueryClient): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || (event.action.type !== "success" && event.action.type !== "error")) return;
    const key = event.query.queryKey;
    if (key.length !== queryKeys.businesses.length || !queryKeys.businesses.every((part, i) => key[i] === part)) return;
    if (event.action.type === "error") setReplayText(false);
    else syncPlaceReplay(event.query.state.data as MyBusiness[]);
  });
}

/** At sign-out: the next account starts with no place, so a later read cannot bring text back. */
export function forgetAppliedPlace(): void {
  appliedPlaceId = null;
}

const LAST_KEY = "teamna-web-last-place";

/** The place this device opened last for this account. Null when none, or another account's. */
export function lastPlaceId(userId: string): string | null {
  try {
    const stored = JSON.parse(window.localStorage.getItem(LAST_KEY) ?? "null") as { userId?: string; placeId?: string } | null;
    return stored?.userId === userId && typeof stored.placeId === "string" ? stored.placeId : null;
  } catch {
    return null;
  }
}

/** Remembers the place on this device (decided with Anas: the device, not the account). */
export function rememberPlace(userId: string, placeId: string): void {
  try {
    window.localStorage.setItem(LAST_KEY, JSON.stringify({ userId, placeId }));
  } catch {
    // Private mode: the question comes back next time, which is still right.
  }
}

/** The words of a place, in the business's own words: "Owner office", "Coach, your sessions", "Parent". */
export function placeRole(place: Place): string {
  const labels = place.business.labels ?? defaultLabels(place.business.sport);
  const word = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  if (place.kind === "owner") {
    if (place.business.staffRole === "frontdesk") return "Front desk";
    return place.business.staffRole === "admin" ? "Admin, office" : "Owner office";
  }
  if (place.kind === "coach") return `${word(labels.staffSingular)}, your sessions`;
  return word(labels.memberSingular);
}
